import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as authService from '../services/authService';
import { AuthContext } from './authContextStore';

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => authService.getJwt());
  const [darkModeDefault, setDarkModeDefault] = useState(() => authService.getDarkModeDefault());
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    // Token reads are synchronous — initialization is immediate.
    setIsInitializing(false);
  }, []);

  const login = useCallback(async ({ email, password } = {}) => {
    const tokens = await authService.login({ email, password });
    setToken(tokens?.token ?? authService.getJwt());
    if (tokens?.darkModeDefault !== null && tokens?.darkModeDefault !== undefined) {
      setDarkModeDefault(tokens.darkModeDefault);
    }
    return tokens;
  }, []);

  /**
   * Self-register accepts the fields from SelfRegisterRequest:
   * firstName, lastName, email, password, lanId (optional), phoneNumber (optional)
   */
  const register = useCallback(async ({ firstName, lastName, email, password, lanId, phoneNumber } = {}) => {
    return authService.selfRegister({ firstName, lastName, email, password, lanId, phoneNumber });
  }, []);

  /**
   * Calls the Auth API logout endpoint (clears the HttpOnly refresh-token cookie)
   * then clears local JWT storage.
   */
  const logout = useCallback(async () => {
    await authService.logout();
    setToken(null);
    setDarkModeDefault(null);
  }, []);

  const refreshFromStorage = useCallback(() => {
    setToken(authService.getJwt());
    setDarkModeDefault(authService.getDarkModeDefault());
  }, []);

  const value = useMemo(() => {
    const isAuthenticated = Boolean(token);
    return {
      token,
      isAuthenticated,
      isInitializing,
      darkModeDefault,
      login,
      register,
      logout,
      refreshFromStorage
    };
  }, [token, isInitializing, darkModeDefault, login, register, logout, refreshFromStorage]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
